import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { parseZone } from 'moment';
import { Photographer, PhotographerBrand, PhotographerCategory } from '../../../models/common/photographer.models';
import { CompanyService } from '../../../services/company.service';
import { Brand } from '../../../models/common/brand.model';
import { AutocompleteSettings } from '../../../shared/models/autocomplete-settings.model';
import { Category } from '../../../models/common/category.model';
import { PhotographerStatus } from '../../../models/common/photographer-status.model';
import { UtilityFunctions } from '../../../shared/utilities/functions.models';
import { PhotographerStatusEnum } from '../../../models/common/photographer-status.enum';
import { UtilitiesService } from '../../../shared/services/utilities.service';
import { UpdatePhotographer } from '../../../models/company/update-photographer.models';
import { BrandIdCategoryId } from '../../../models/company/brand-id-category-id.model';
import { GraphQLService } from '../../../services/graph-ql.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GetQuery } from '../../../models/shared/enums/get-query.enum';
import { map } from 'rxjs';
import { AppService } from '../../../app.service';
import { WrapperResultGraphQL } from '../../../models/shared/types/wrapper-result-graphql.type';
import { MutationQuery } from '../../../models/shared/enums/mutation-query.enum';

@Component({
  standalone: false,
  selector: 'bt-sidebar-editinfo',
  templateUrl: './sidebar-editinfo.component.html',
  styleUrls: ['./sidebar-editinfo.component.scss']
})
export class SidebarEditinfoComponent implements OnChanges, OnInit {

  @Input() public photographer: Photographer;
  @Output() public saved = new EventEmitter<Photographer>();

  readonly #companyService = inject(CompanyService);
  readonly #utilitiesService = inject(UtilitiesService);
  readonly #graphQLService = inject(GraphQLService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #appService = inject(AppService);


  public hiredDate: string;
  public brands: { data: Array<Brand>, selected?: Brand, autocompleteSettings: AutocompleteSettings, loaderId: string } = {
    autocompleteSettings: new AutocompleteSettings({ textProperty: 'brandName', valueProperty: 'brandId' }),
    loaderId: 'brandsLoaderId',
    data: []
  };
  public categories: { data: Array<Category>, selected?: Category, autocompleteSettings: AutocompleteSettings, loaderId: string } = {
    autocompleteSettings: new AutocompleteSettings({
      textProperty: 'name',
      valueProperty: 'id',
      placeholder: '- All categories -'
    }),
    loaderId: 'categoriesLoaderId',
    data: []
  };

  public status: { data: Array<PhotographerStatus>, selected?: PhotographerStatus, autocompleteSettings: AutocompleteSettings } = {
    autocompleteSettings: new AutocompleteSettings({ textProperty: 'name', valueProperty: 'id' }),
    data: []
  };

  public udapteLoaderId = UtilityFunctions.newGuid();
  public loaderId = UtilityFunctions.newGuid();
  public statusIdToSetHiredData = PhotographerStatusEnum.Hired;
  public todayDate: string;

  private cachedPhotographer: Photographer;

  constructor() {
    this.todayDate = parseZone(new Date()).format('yyyy-MM-DD');
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.photographer && this.photographer) {
      this.cachedPhotographer = UtilityFunctions.DeepClone(this.photographer);
      this.hiredDate = parseZone(this.cachedPhotographer.hiredUntil).format('yyyy-MM-DD');
      this.status.selected = this.photographer.statusId ? {
        id: this.photographer.statusId,
        name: this.photographer.statusName
      } : undefined;
    }
  }

  public ngOnInit() {
    this.getPhotographerStatus();
  }

  public onChangeDate() {
    this.photographer.hiredUntil = parseZone(this.hiredDate).utc(true).toISOString();
  }

  public onSelectBrand(b: Brand) {
    this.brands.selected = b;
    !this.brands.selected ? this.categories.selected = null : this.getCategories();
  }

  public onSelectStatus(s: PhotographerStatus) {
    this.status.selected = s;
    this.photographer.statusId = s ? s.id : undefined;
    this.photographer.statusName = s ? s.name : undefined;
  }

  public onSelectCategory(c: Category) {
    this.categories.selected = c;
  }

  public removeBrand(b: PhotographerBrand, c?: PhotographerCategory) {
    this.brands.selected = undefined;
    this.photographer.brands = this.photographer.brands.filter(x => {
      let flag = true;
      if (!c) {
        flag = x.brandId !== b.brandId;
      } else if (x.brandId === b.brandId) {
        b.categories = b.categories.filter(cat => c.categoryId !== cat.categoryId);
        flag = !!b.categories.length;
      }
      return flag;
    });
  }

  public addBrand() {
    this.photographer.brands = this.photographer.brands ? this.photographer.brands : []

    let brand = this.photographer.brands?.find(x => x.brandId == this.brands.selected!.brandId);
    if (!brand) {
      brand = new PhotographerBrand({
        brandId: this.brands.selected!.brandId,
        brandName: this.brands.selected!.brandName,
        categories: this.categories.selected ? [new PhotographerCategory({
          brandId: this.brands.selected!.brandId,
          personId: this.photographer.personId,
          categoryId: this.categories.selected.id,
          categoryName: this.categories.selected.name
        })] : []
      });

      this.photographer.brands.push(brand);
      this.brands.selected = undefined;
      this.categories.selected = undefined;
    } else if (this.categories.selected && !!brand.categories.length && !brand.categories.some(x => x.categoryId === this.categories.selected!.id)) {
      brand.categories.push(new PhotographerCategory({
        brandId: brand.brandId,
        personId: this.photographer.personId,
        categoryId: this.categories.selected.id,
        categoryName: this.categories.selected.name
      }));
      this.brands.selected = undefined;
      this.categories.selected = undefined;
    } else {
      this.#utilitiesService.showSnackbar('Addition conflicts with already present data');
    }
  }

  public onSave() {
    if (this.checkFields()) {
      // const updatePhotographer = new UpdatePhotographer({
      //   PhotographerId: this.photographer.personId,
      //   DiscountPercentage: this.photographer.discountPercentage,
      //   DiscountThreshold: this.photographer.discountThreshold,
      //   EndDate: this.photographer.hiredUntil || undefined,
      //   StartDate: this.photographer.statusId !== this.cachedPhotographer.statusId && this.photographer.statusId === this.statusIdToSetHiredData ? parseZone(new Date()).utc(true).toISOString() : undefined,
      //   MaxItems: this.photographer.maxItems,
      //   ShotCost: this.photographer.shotCost,
      //   StatusId: this.photographer.statusId,
      //   AutomaticRenewal: this.photographer.automaticRenewal,
      //   BrandIdCategoryId: []
      // });


      const updatePhotographer: {
        currentUserId: number,
        companyId: number,
        photographerId?: number,
        automaticRenewal: boolean,
        brandIdCategoryId: Array<{ brandId?: number, categoryId?: number }>,
        statusId?: number,
        shotCost?: number,
        maxItems?: number,
        discountThreshold?: number,
        discountPercentage?: number,
        startDate?: string,
        endDate?: string
      } = {
        currentUserId: this.#appService.user.userId,
        companyId: this.#appService.companyId,
        photographerId: this.photographer.personId,
        automaticRenewal: !!this.photographer.automaticRenewal,
        brandIdCategoryId: [],
        statusId: this.photographer.statusId,
        shotCost: this.photographer.shotCost,
        maxItems: this.photographer.maxItems,
        discountThreshold: this.photographer.discountThreshold,
        discountPercentage: this.photographer.discountPercentage,
        startDate: this.photographer.statusId !== this.cachedPhotographer.statusId && this.photographer.statusId === this.statusIdToSetHiredData ? parseZone(new Date()).utc(true).toISOString() : undefined,
        endDate: this.photographer.hiredUntil || undefined
      };

      this.photographer.brands!.forEach(b => {
        b.categories && b.categories.length ?
          b.categories.forEach(c =>
            updatePhotographer.brandIdCategoryId.push({ brandId: b.brandId, categoryId: c.categoryId })) :
          updatePhotographer.brandIdCategoryId.push({ brandId: b.brandId, categoryId: undefined });
      });

      this.#graphQLService.postData(MutationQuery.EDIT_PHOTOGRAPHER, updatePhotographer)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: () => this.saved.emit(this.photographer),
          error: (err) =>  this.#utilitiesService.showSnackbar(err.errors.message)
        });
    } else {
      this.#utilitiesService.showSnackbar('Incorrect or missing fields');
    }
  }

  private getBrands() {
    this.#graphQLService.getData<Brand>(GetQuery.GET_BRANDS_NO_IDS, { search: '', companyId: Number(this.#appService.companyId), first: 50, after: null })
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map(res => res['data']['brands'])
      )
      .subscribe({
        next: (res: WrapperResultGraphQL<any>) => {
          this.brands.data = res.nodes.map(el => ({ brandId: el.brandId, brandName: el.brand.name, hasSizeGroup: el.hasSizeGroup, displayId: el.brandId!, displayName: el.brand.name }));
        }
      })
  }

  private getCategories() {
    this.categories.data = [];
    //this.#companyService.getCategories(this.brands.selected.brandId, this.categories.loaderId).subscribe(data => this.categories.data = data);
    console.log(this.categories)
    const filters = { search: '', companyId: this.#appService.companyId, first: null, after: null, brandIds: [this.brands.selected?.brandId] };

    this.#graphQLService.getData<Category>(GetQuery.GET_CATEGORIES_NO_IDS, filters)
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map(res => res['data']['category'])
      )
      .subscribe({
        next: (res: WrapperResultGraphQL<Category>) => {
          this.categories.data = res.nodes.map(el => ({ ...el }));
        }
      })
  }

  private getPhotographerStatus() {
    this.#graphQLService.getData<PhotographerStatus>(GetQuery.GET_PHOTOGRAPHER_STATUS_NO_IDS)
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['photographerStatus']))
      .subscribe({
        next: (data) => {
          this.status.data = data.nodes.filter(s => s.isCompanySelectable).map(el => ({ ...el, disabled: false, selected: true, displayId: el.id, displayName: el.name }));
          if (this.status.selected && !this.status.data.some(x => x.id === this.status.selected!.id)) this.status.selected = undefined;
          this.getBrands();
        }
      });
  }

  private checkFields(): boolean {
    return (this.photographer.statusId !== this.statusIdToSetHiredData || !!this.photographer.hiredUntil) && this.photographer.maxItems > 0;
  }
}
